import type { ReactElement, ReactNode } from 'react';
import { Drawer } from 'vaul';
import styles from './Sheet.module.css';

export interface SheetProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description?: string | undefined;
  /** Optional element that opens the sheet (uncontrolled-style usage from a trigger). */
  readonly trigger?: ReactElement | undefined;
  readonly footer?: ReactNode | undefined;
  readonly children: ReactNode;
}

/** Bottom sheet for phones: swipe down or tap outside to dismiss, actions in the thumb zone. */
export const Sheet = ({ open, onOpenChange, title, description, trigger, footer, children }: SheetProps) => (
  <Drawer.Root open={open} onOpenChange={onOpenChange} repositionInputs={false}>
    {trigger && <Drawer.Trigger asChild>{trigger}</Drawer.Trigger>}
    <Drawer.Portal>
      <Drawer.Overlay className={styles.overlay} />
      <Drawer.Content
        className={styles.content}
        // On phones the keyboard would cover half the sheet: nothing is focused until tapped.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement).focus();
        }}
        {...(description ? {} : { 'aria-describedby': undefined })}
      >
        <Drawer.Handle className={styles.handle} />
        <header className={styles.header}>
          <Drawer.Title className={styles.title}>{title}</Drawer.Title>
          {description && (
            <Drawer.Description className={styles.description}>{description}</Drawer.Description>
          )}
        </header>
        <div className={styles.body}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </Drawer.Content>
    </Drawer.Portal>
  </Drawer.Root>
);
